import { getLocalizationInfo } from 'project.config';
import { ComposableCommerce, ComposableCommerceEvents } from './composable-commerce';
import { CookieHandler, ServerOptions, SDK } from '@commercetools/frontend-sdk';
import { getContextAwareCookieOptions, isEmbeddedBrowserContext } from 'helpers/cookies';

const cookieHandler = new CookieHandler();

// Add other integration's custom events to the SDK's generic type here,
// by extending ComposableCommerceEvents with their type using an intersection.
// For example, <ComposableCommerceEvents & OtherEvents>.
// You may also wish to add your own custom events.
class CommercetoolsSDK extends SDK<ComposableCommerceEvents> {
  composableCommerce!: ComposableCommerce;
  // Add any other integrations here.

  constructor() {
    super();
    this.composableCommerce = new ComposableCommerce(this);
    // Initialize your other integrations here.

    this.on('errorCaught', (event) => {
      // Globally handle any errors caught by the SDK and integrations. For
      // example, log error, fire notification, etc.
      console.info(`SDK error: `, event.data);
    });

    this.on('fetchCalled', (event) => {
      console.info(`${event.data.type === 'action' ? event.data.actionName : event.data.method} called: `, event.data);
    });

    this.on('fetchSuccessful', (event) => {
      console.info(
        `${event.data.type === 'action' ? event.data.actionName : event.data.method}  successful: `,
        event.data,
      );
    });

    // Set up any other custom global event handlers here.
    // Ensure types are created and added to the SDK generic type
    // if specific to your project.
  }

  // A simplified, reusable method for configuring the SDK, as for
  // most cases only the locale and currency require input from runtime, or
  // may change on user input.
  defaultConfigure(localeString: string) {
    const { locale, currency } = getLocalizationInfo(localeString);

    this.configure({
      locale: locale.replace('-', '_'),
      cookieHandlingOverride: {
        setCookie: async (key: string, data: any, options?: any) => {
          const isEmbedded = isEmbeddedBrowserContext();
          const contextAwareCookieOptions = getContextAwareCookieOptions({
            isEmbedded,
            maxAge: options?.maxAge,
          });
          const { sameSite, secure, partitioned } = contextAwareCookieOptions;

          cookieHandler.setCookie(key, data, {
            ...options,
            sameSite,
            secure,
            ...(partitioned ? { partitioned } : {}),
          });
        },
        getCookie: async (key: string, options?: ServerOptions) => {
          return cookieHandler.getCookie(key, options);
        },
        getCookies: async (options?: ServerOptions) => {
          return cookieHandler.getCookies(options);
        },
        deleteCookie: async (key: string, options?: ServerOptions) => {
          return cookieHandler.deleteCookie(key, options);
        },
        hasCookie: async (key: string, options?: ServerOptions) => {
          return cookieHandler.hasCookie(key, options);
        },
      },
      currency,
      extensionVersion: process.env.NEXT_PUBLIC_EXT_BUILD_ID ?? 'dev',
      endpoint: process.env.NEXT_PUBLIC_FRONTASTIC_HOST
        ? (process.env.NEXT_PUBLIC_FRONTASTIC_HOST as string).split('/frontastic')[0]
        : '',
      // Sent as the "coFE-Custom-Configuration" header on every API Hub call.
      // Must match the header_value configured in the commercetools firewall support ticket.
      customHeaderValue: process.env.NEXT_PUBLIC_FIREWALL_HEADER_VALUE,
    });
  }
}

// Create a single instance of the sdk.
const sdk = new CommercetoolsSDK();

// Export only the instance to serve as a singleton throughout
// the project.
export { sdk };
